import { useState } from 'react';

import { useAppStore } from "../../hooks/useAppStore";

import {
  Box,
  Grid,
  Typography,
  Card,
  CardActionArea,
  CardMedia,
  CardContent
} from '@mui/material';

import { ReactComponent as GridLayoutImage } from '../../assets/images/layout-grid.svg';
import { ReactComponent as IslandLayoutImage } from '../../assets/images/layout-island.svg';
import { ReactComponent as CockpitLayoutImage } from '../../assets/images/layout-cockpit.svg';
import { ReactComponent as CustomLayoutImage } from '../../assets/images/layout-custom.svg';

import { ReactComponent as HexagonPreview } from '../../assets/images/preview-hexagon.svg';
import { ReactComponent as RhombusOneButtonPreview } from '../../assets/images/preview-rhombus1.svg';
import { ReactComponent as RhombusTwoButtonsPreview } from '../../assets/images/preview-rhombus2.svg';
import { ReactComponent as RectanglePreview } from '../../assets/images/preview-rectangle.svg';


const panelShapeOptions = [
  { id: 'rectangle', label: 'Rectangle', Component: RectanglePreview },
  { id: 'hexagon', label: 'Hexagon', Component: HexagonPreview },
  { id: 'rhombus1', label: 'Rhombus (One button)', Component: RhombusOneButtonPreview },
  { id: 'rhombus2', label: 'Rhombus (Two buttons)', Component: RhombusTwoButtonsPreview },
  //{ id: 'other', label: 'Other', Component: QuestionMark },
];

const layoutOptions = [
  {
    id: 'grid',
    label: 'Grid',
    component: GridLayoutImage,
    description: ['Aligned in predictable patterns'],
    pros: ['Easy to memorize', 'Structured rows/columns'],
    cons: ['Less flexible with categories']
  },
  {
    id: 'island',
    label: 'Island',
    component: IslandLayoutImage,
    description: ['Arranged in compacted groups'],
    pros: ['Group words by theme', 'Good for multiple pets'],
    cons: ['Takes more space']
  },
  {
    id: 'cockpit',
    label: 'Cockpit',
    component: CockpitLayoutImage,
    description: ['Curved layout like control panel'],
    pros: ['Easy for pet to have eye contact', 'Visually engaging'],
    cons: ['Harder to set up physically']
  },
  {
    id: 'bridge',
    label: 'Bridge',
    component: CustomLayoutImage,
    description: ['An empty path in between panels of buttons for easy manuveuring on the soundboard'],
    pros: ['Pet can stay on their soundboard'],
    cons: ['Harder to plan']
  },
  {
    id: 'custom',
    label: 'Design it myself',
    component: CustomLayoutImage,
    description: ['The popular shapes don\'t fit your needs and you need more control'],
    pros: ['You can customize it however you like'],
    cons: ['Harder to plan']
  }
];

const spacingOptions = [
  {
    id: 'spaced',
    label: 'Spaced',
    component: RectanglePreview,
    description: [
      'Leaves some positions empty before moving to the next panel.',
      'Easier to learn for pets, less crowded.',
    ],
    pros: ['Encourages clearer distinction between buttons', 'Better for early learners'],
    cons: ['May require more panels', 'Takes up more space'],
  },
  {
    id: 'packed',
    label: 'Packed',
    component: HexagonPreview,
    description: [
      'Fills every available position on a panel before creating a new one.',
      'Efficient use of space.'
    ],
    pros: ['Uses fewer panels', 'Maximizes button density'],
    cons: ['Buttons may be too close for beginners', 'Harder to reorganize'],
  },
];

export default function SoundboardLayoutStep() {

  const {
    selectedWords, setSelectedWords,
    selectedLayout, setSelectedLayout,
    selectedShape, setSelectedShape,
    selectedSpacing, setSelectedSpacing,
    resetSetup,
  } = useAppStore();


  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Choose the shape of panels you are using
      </Typography>

      <Grid container spacing={2}>
        {panelShapeOptions.map((option) => (
          <Grid item xs={12} sm={6} md={3} key={option.id}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                aspectRatio: '1 / 1', // keeps square shape
                borderRadius: '24px',
                borderColor: selectedShape === option.id ? 'primary.main' : 'grey.300',
                borderWidth: selectedShape === option.id ? 2 : 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <CardActionArea onClick={() => setSelectedShape(option.id)} sx={{ height: '100%' }}>
                <CardContent
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 2,
                    textAlign: 'center',
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                  {option.Component && (
                    <option.Component
                      style={{
                        width: 90,
                        height: 90,
                        color: selectedShape === option.id ? '#FFCE50' : '#5b2a84',
                      }}
                    />
                  )}                    
                    {/*<img
                      src={option.image}
                      alt={`${option.label} preview`}
                      style={{ height: 60, width: 60, objectFit: 'contain' }}
                    />*/}
                  </Box>
                  <Typography variant="body1" fontWeight="bold">
                    {option.label}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" gutterBottom>
        Choose your soundboard layout
      </Typography>
      <Typography variant="body1" mb={4}>
        Pick a layout that fits your pet’s space and learning style.
      </Typography>

      <Grid container spacing={3}>
        {layoutOptions.map((option) => (
          <Grid item xs={12} sm={6} key={option.id}>
            <Card
            sx={{
              borderRadius: '50px',
              border:
              selectedLayout === option.id ? '2px solid #1976d2' : '1px solid #ccc',
              borderWidth: 2,
              overflow: 'hidden' // ensures image respects the rounded border
            }}
            >
              <CardActionArea onClick={() => setSelectedLayout(option.id)}>

              {option.component && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 160,
                    color: selectedLayout === option.id ? '#FFCE50' : '#5b2a84',
                  }}
                >
                  <option.component style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </Box>
              )}
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {option.label}
                  </Typography>
                  <ul>
                    {option.description.map((description, i) => (
                      <li key={i}>
                        <Typography variant="body2">{description}</Typography>
                      </li>
                    ))}
                  </ul>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Pros:
                  </Typography>
                  <ul>
                    {option.pros.map((pro, i) => (
                      <li key={i}>
                        <Typography variant="body2">{pro}</Typography>
                      </li>
                    ))}
                  </ul>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Cons:
                  </Typography>
                  <ul>
                    {option.cons.map((con, i) => (
                      <li key={i}>
                        <Typography variant="body2">{con}</Typography>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Typography variant="h5" gutterBottom>
        Choose how your panels fill up
      </Typography>
      <Typography variant="body1" mb={4}>
        Decide whether to fill all button spots before starting a new panel, or leave room on each panel for flexibility.
      </Typography>

      <Grid container spacing={3}>
          {spacingOptions.map((option) => (
            <Grid item xs={12} sm={6} key={option.id}>
              <Card
                sx={{
                  borderRadius: '50px',
                  border: selectedSpacing === option.id
                    ? '2px solid #1976d2'
                    : '1px solid #ccc',
                  overflow: 'hidden',
                }}
              >
                <CardActionArea onClick={() => setSelectedSpacing(option.id)}>
                {option.component && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 160,
                      color: selectedSpacing === option.id ? '#FFCE50' : '#5b2a84',
                    }}
                  >
                    <option.component style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </Box>
                )}
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {option.label}
                    </Typography>
                    <ul>
                      {option.description.map((desc, i) => (
                        <li key={i}>
                          <Typography variant="body2">{desc}</Typography>
                        </li>
                      ))}
                    </ul>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Pros:
                    </Typography>
                    <ul>
                      {option.pros.map((pro, i) => (
                        <li key={i}>
                          <Typography variant="body2">{pro}</Typography>
                        </li>
                      ))}
                    </ul>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Cons:
                    </Typography>
                    <ul>
                      {option.cons.map((con, i) => (
                        <li key={i}>
                          <Typography variant="body2">{con}</Typography>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
  </Box>
  );
}